import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, ShieldOff, Send, Search } from 'lucide-react';
import { Button } from './ui/button';
import { PropertyCard, PropertyCardData } from './PropertyCard';
import { FilterState, resolveCategories } from './FilterBar';
import { CategoryFilter } from './CategoryFilter';
import { CONTENT_WIDTH } from './layout.constants';
import { Property } from '../../types';
import { propertyService } from '../../services/propertyService';
import { offeringsService, Offering } from '../../services/offerings';
import { transactionsApi } from '../../services/api';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { useKycGate } from './KycGate';
import { PublicHeader, INVEST_NAV_ITEMS } from '../Layout';
import { PropertyDetail } from './PropertyDetails/PropertyDetail';
import { PropertyExtendedData } from './PropertyDetails/PropertyDetail.types';
import { OfferingDetail } from './OfferingDetail';
import { TENANT } from '../../config';
import { InvestmentFlow } from './Invest';
import type { InvestmentProperty } from './Invest/InvestmentFlow.types';
import toast from 'react-hot-toast';

interface InvestLayoutProps {
  isPublic?: boolean;
}

// Helper function to convert Property to PropertyCardData
const convertToPropertyCardData = (property: Property): PropertyCardData => {
  const totalShares = property.total_shares || 1;
  const availableShares = property.available_shares ?? totalShares;
  const sharesSold = property.shares_sold || (totalShares - availableShares);
  const investorCount = property.stakersCount || 0;
  const fundedPct = property.completion_percentage || Math.round((sharesSold / totalShares) * 100);
  // Derive funded status dynamically from available shares — not hardcoded
  const isFunded = availableShares === 0;
  return {
    id: property.id,
    name: property.name || property.title || property.address,
    address: property.address,
    city: property.city,
    state: property.state,
    imageUrl: property.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&auto=format&fit=crop&q=80',
    propertyType: property.property_type || 'single-family',
    price: property.total_value,
    totalShares: totalShares,
    availableShares: availableShares,
    targetReturn: property.annual_yield * 100,
    investorCount: investorCount,
    fundedPercentage: isFunded ? 100 : fundedPct,
    status: isFunded ? 'funded' : 'available',
    tags: property.tags || [],
    tabContent: property.propertyData?.tab_content || {},
    fieldVisibility: property.propertyData?.fieldVisibility,
  };
};

// Helper function to convert Property to PropertyExtendedData for detail view
const convertToPropertyExtendedData = (property: Property): PropertyExtendedData => {
  const cardData = convertToPropertyCardData(property);
  return {
    ...cardData,
    images: property.images || [cardData.imageUrl],
    description: property.description,
    tabContent: property.propertyData?.tab_content || {},
  };
};

/**
 * Convert an API Offering to a PropertyCardData for display in the grid.
 */
const convertOfferingToCard = (offering: Offering): PropertyCardData => {
  const kpis = offering.kpis || {};
  const branding = offering.branding || {};
  const settings = offering.settings || {};
  const isLicense = offering.type === 'license' || settings.displayAs === 'license-offer';
  const isMining = offering.type === 'mining' || settings.displayAs === 'mining-node';

  if (isMining) {
    return {
      id: offering.id,
      name: offering.title,
      address: offering.summary || '',
      city: '',
      state: '',
      imageUrl: (branding.imageUrl as string) || 'https://images.unsplash.com/photo-1639762681057-408e52192e55?w=1600&auto=format&fit=crop&q=80',
      propertyType: 'mining',
      price: offering.raiseTarget || 0,
      totalShares: 100, // percentage representation
      availableShares: 100,
      targetReturn: (kpis.emission_share as number) || 0,
      investorCount: 0,
      fundedPercentage: offering.raiseTarget ? Math.round(((offering.raised || 0) / offering.raiseTarget) * 100) : 0,
      status: 'available',
      tags: ['mining', ...(offering.tags || [])],
    };
  }

  if (isLicense) {
    const totalSlots = (kpis.total_slots as number) || 4;
    const availableSlots = (kpis.available_slots as number) || 3;
    const filledSlots = totalSlots - availableSlots;
    return {
      id: offering.id,
      name: offering.title,
      address: offering.summary || '',
      city: '',
      state: '',
      imageUrl: (branding.imageUrl as string) || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&auto=format&fit=crop&q=80',
      propertyType: offering.type,
      price: (kpis.annual_allocation as number) || offering.minInvest || 0,
      totalShares: totalSlots,
      availableShares: availableSlots,
      targetReturn: 0,
      investorCount: filledSlots,
      fundedPercentage: Math.round((filledSlots / totalSlots) * 100),
      status: 'available',
      tags: ['fund', 'license-offer', ...(offering.tags || [])],
    };
  }

  // Fund / generic offering
  return {
    id: offering.id,
    name: offering.title,
    address: offering.summary || '',
    city: (branding.location as string)?.split(',')[0]?.trim() || '',
    state: (branding.location as string)?.split(',')[1]?.trim() || '',
    imageUrl: (branding.imageUrl as string) || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&auto=format&fit=crop&q=80',
    propertyType: offering.type,
    price: (kpis.total_value as number) || offering.raiseTarget || 0,
    totalShares: (kpis.total_shares as number) || 1,
    availableShares: (kpis.available_shares as number) || 0,
    targetReturn: (kpis.target_return as number) || 0,
    investorCount: (kpis.investor_count as number) || 0,
    fundedPercentage: (kpis.funded_percentage as number) || 0,
    status: 'available',
    tags: ['fund', ...(offering.tags || [])],
    fieldVisibility: settings.fieldVisibility as any,
  };
};

// Helper function to convert Property to InvestmentProperty for the investment flow
const convertToInvestmentProperty = (property: Property): InvestmentProperty => {
  const sharePrice = property.total_value / (property.total_shares || 1);
  return {
    id: property.id,
    title: property.name || property.title || property.address,
    location: `${property.city}, ${property.state}`,
    image: property.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&auto=format&fit=crop&q=80',
    pricePerShare: sharePrice,
    annualYield: property.annual_yield, // Already in decimal format from backend
    availableShares: property.available_shares,
  };
};

export function InvestLayout({ isPublic = false }: InvestLayoutProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUser();
  const { requireKyc, KycGateModal } = useKycGate();

  // Extract property ID from route params if present (e.g., /portal/invest/properties/:propertyId)
  const { '*': investSplat } = useParams<{ '*': string }>();
  const selectedPropertyId = useMemo(() => {
    if (!investSplat) return null;
    const match = investSplat.match(/^properties\/([^/]+)$/);
    return match ? match[1] : null;
  }, [investSplat]);

  // Properties state
  const [properties, setProperties] = useState<Property[]>([]);
  const [offeringCards, setOfferingCards] = useState<PropertyCardData[]>([]);
  const [categories, setCategories] = useState(TENANT.investCategories);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    propertyType: 'all',
    searchQuery: '',
    minPrice: 0,
    maxPrice: 10000000,
    location: '',
  });

  // Investment state
  const [investmentLoading, setInvestmentLoading] = useState(false);
  const [investmentSuccess, setInvestmentSuccess] = useState(false);

  // Investment flow state
  const [showInvestmentFlow, setShowInvestmentFlow] = useState(false);
  const [investmentProperty, setInvestmentProperty] = useState<InvestmentProperty | null>(null);

  useEffect(() => {
    let active = true;
    fetchAll(() => active);
    return () => { active = false; };
  }, []);

  // `isActive` lets the mount effect discard a stale response if the component unmounts mid-fetch.
  const fetchAll = async (isActive: () => boolean = () => true) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch properties, offerings, and categories in parallel
      const [propertyData, offerings, cats] = await Promise.all([
        propertyService.getProperties().catch(() => []),
        offeringsService.getOfferings().catch(() => []),
        offeringsService.getCategories().catch(() => TENANT.investCategories),
      ]);
      if (!isActive()) return;

      const propertyArray = Array.isArray(propertyData) ? propertyData : [];
      setProperties(propertyArray);

      // Convert API offerings to card format
      const cards = offerings.map(convertOfferingToCard);
      setOfferingCards(cards);

      // Use dynamic categories
      if (cats.length > 0) setCategories(cats);
    } catch (err) {
      if (!isActive()) return;
      console.error('[InvestLayout] Failed to fetch data:', err);
      setProperties([]);
      setOfferingCards([]);
      setError('Unable to load investments. Please try again later.');
    } finally {
      if (isActive()) setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await propertyService.getProperties();
      const propertyArray = Array.isArray(data) ? data : [];
      setProperties(propertyArray);
    } catch (err) {
      console.error('[InvestLayout] Failed to fetch properties:', err);
      setProperties([]);
      setError('Unable to load properties. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Filter function for PropertyCardData (works for both properties and funds)
  const applyCardFilters = (card: PropertyCardData): boolean => {
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesSearch =
        card.name.toLowerCase().includes(query) ||
        card.address.toLowerCase().includes(query) ||
        card.city.toLowerCase().includes(query) ||
        card.state.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    if (card.price < filters.minPrice || card.price > filters.maxPrice) {
      return false;
    }

    if (filters.location) {
      const locationFilter = filters.location.toLowerCase();
      const matchesLocation =
        card.city.toLowerCase().includes(locationFilter) ||
        card.state.toLowerCase().includes(locationFilter);
      if (!matchesLocation) return false;
    }

    if (filters.propertyType !== 'all') {
      // Check if propertyType matches OR if tags include the filter
      const normalizedType = card.propertyType?.toLowerCase().replace(/\s+/g, '-');
      const matchesType = normalizedType === filters.propertyType;
      const matchesTags = card.tags?.includes(filters.propertyType) || false;
      if (!matchesType && !matchesTags) {
        return false;
      }
    }

    return true;
  };

  // Convert properties to cards and prepend offerings from API
  const allCards: PropertyCardData[] = [
    ...offeringCards,
    ...properties.map(convertToPropertyCardData),
  ];

  const filteredCards = allCards.filter(applyCardFilters);

  // Find the selected property for detail view
  const selectedProperty = useMemo(() => {
    if (!selectedPropertyId) return null;
    const property = properties.find(p => p.id === selectedPropertyId);
    return property ? convertToPropertyExtendedData(property) : null;
  }, [selectedPropertyId, properties]);

  // Handle closing the property detail view
  const handleCloseDetail = () => {
    navigate('/portal/invest/properties');
  };

  // State for showing Featured Offer detail overlay
  const [showFeaturedOfferDetail, setShowFeaturedOfferDetail] = useState(false);
  const [selectedOfferingCard, setSelectedOfferingCard] = useState<PropertyCardData | null>(null);

  // State for showing property detail to unauthenticated users (no URL navigation needed)
  const [publicDetailProperty, setPublicDetailProperty] = useState<PropertyExtendedData | null>(null);

  // Handle closing the Hive Fund detail view
  const handleCloseFeaturedOffer = () => {
    setShowFeaturedOfferDetail(false);
    setSelectedOfferingCard(null);
  };

  // Handle closing the public (unauth) property detail overlay
  const handleClosePublicDetail = () => {
    setPublicDetailProperty(null);
  };

  // Handle "Invest" clicked while unauthenticated — send to login then return to the property
  const handlePublicInvest = () => {
    if (!publicDetailProperty) return;
    navigate('/login', {
      state: { returnTo: `/portal/invest/properties/${publicDetailProperty.id}` },
    });
  };

  // Handle opening the investment flow from PropertyDetail
  const handleOpenInvestFlow = () => {
    if (!requireKyc()) return;
    if (!selectedPropertyId) return;
    const property = properties.find(p => p.id === selectedPropertyId);
    if (!property) return;

    // Check if user is non-accredited — show request access modal instead
    const accredStatus = user?.accreditation_status;
    const hasAccessToken = !!sessionStorage.getItem('investAccessToken');
    if (accredStatus === 'self_declared_non_accredited' && !hasAccessToken) {
      setShowAccessRequestModal(true);
      setAccessRequestPropertyId(property.id);
      return;
    }

    setInvestmentProperty(convertToInvestmentProperty(property));
    setShowInvestmentFlow(true);
  };

  // Access request state
  const [showAccessRequestModal, setShowAccessRequestModal] = useState(false);
  const [accessRequestPropertyId, setAccessRequestPropertyId] = useState<string | null>(null);
  const [accessRequestMessage, setAccessRequestMessage] = useState('');
  const [accessRequestSubmitting, setAccessRequestSubmitting] = useState(false);

  const handleSubmitAccessRequest = async () => {
    if (!accessRequestPropertyId) return;
    setAccessRequestSubmitting(true);
    try {
      const res: any = await api.post('/offering-access/request', {
        dealId: accessRequestPropertyId,
        message: accessRequestMessage || undefined,
      });
      const d = res?.data || res;
      if (d.success !== false) {
        toast.success('Access request submitted. You will be notified when approved.');
        setShowAccessRequestModal(false);
        setAccessRequestMessage('');
      } else {
        toast.error(d.message || 'Failed to submit request');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to submit request';
      toast.error(msg);
    } finally {
      setAccessRequestSubmitting(false);
    }
  };

  // Handle closing the investment flow
  const handleCloseInvestFlow = () => {
    setShowInvestmentFlow(false);
    setInvestmentProperty(null);
  };

  // Handle property investment completion
  // Called after InvestmentFlow completes successfully
  // The API call is handled inside InvestmentFlow, so this just refreshes the UI
  const handlePropertyInvest = async () => {
    try {
      setInvestmentLoading(true);
      setInvestmentSuccess(true);

      // Close the investment flow
      setShowInvestmentFlow(false);
      setInvestmentProperty(null);

      // Refetch properties to update available shares
      await fetchProperties();

      // Auto-close detail after a brief success display
      setTimeout(() => {
        setInvestmentSuccess(false);
        handleCloseDetail();
      }, 2000);
    } catch (error: any) {
      console.error('Error refreshing properties:', error);
    } finally {
      setInvestmentLoading(false);
    }
  };

  const handleCardClick = (card: PropertyCardData) => {
    const isOffering = card.tags?.includes('fund') || card.tags?.includes('license-offer') || card.tags?.includes('mining') ||
      card.propertyType === 'fund' || card.propertyType === 'license' || card.propertyType === 'mining';

    if (isOffering) {
      // Offering/fund cards: show detail overlay for both auth and unauth users
      setSelectedOfferingCard(card);
      setShowFeaturedOfferDetail(true);
    } else if (!isAuthenticated) {
      // Unauthenticated users: show property detail overlay in-place.
      // The "Invest" button inside PropertyDetail will redirect to login.
      const property = properties.find(p => p.id === card.id);
      if (property) {
        setPublicDetailProperty(convertToPropertyExtendedData(property));
      }
    } else {
      navigate(`/portal/invest/properties/${card.id}`);
    }
  };

  return (
    <div className={isPublic ? 'min-h-screen bg-white' : ''}>
      {isPublic && <PublicHeader navItems={INVEST_NAV_ITEMS} />}

      {/* Property component — filter + list as one bordered white card (the gray-ground/white-card
          theme lands later on the themes branch; for now it's white-on-white). */}
      <div className={`${CONTENT_WIDTH} pt-6 md:pt-8 pb-6 md:pb-8`}>
        {/* Bordered, inset card on desktop; full-width edge-to-edge on mobile (no double gutter, so
            the list keeps the same width as the nav on a phone). */}
        <div className="bg-card sm:rounded-2xl sm:border sm:border-border">
          {/* Sticky filter header — the card's header; pins under the nav on scroll. Hidden while a
              property-detail overlay is open. */}
          {!((selectedProperty && !showInvestmentFlow) || publicDetailProperty) && (
            <div
              className="sticky z-30 border-b border-border bg-card px-0 py-3 sm:rounded-t-2xl sm:px-6"
              style={{ top: 'var(--invest-filter-top, 0px)' }}
            >
              <CategoryFilter
                value={filters.propertyType}
                onChange={(v) => setFilters({ ...filters, propertyType: v })}
                categories={resolveCategories(categories)}
              />
            </div>
          )}

          <div className="px-0 py-5 sm:px-6">
            {/* Search scrolls with the content (only the chips pin above). */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search by city, state, or property..."
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                className="w-full rounded-md border border-border bg-background py-2.5 pl-10 pr-4 text-foreground placeholder:text-muted-foreground transition-all focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {filteredCards.length > 0 && (
              <div className="mt-4 text-sm text-muted-foreground">
                Showing {filteredCards.length} {filteredCards.length === 1 ? 'property' : 'properties'}
              </div>
            )}

            <div className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-primary" size={40} />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <AlertCircle className="text-destructive" size={48} />
                <p className="text-muted-foreground text-center">{error}</p>
                <Button onClick={() => fetchAll()} variant="outline">
                  Try Again
                </Button>
              </div>
            ) : filteredCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <div className="w-16 h-16 bg-card rounded-full flex items-center justify-center border border-border">
                  <AlertCircle className="text-muted-foreground" size={32} />
                </div>
                <p className="text-muted-foreground text-center">
                  No properties match your filters
                </p>
                {(filters.searchQuery || filters.minPrice > 0 || filters.propertyType !== 'all') && (
                  <Button
                    onClick={() => setFilters({
                      propertyType: 'all',
                      searchQuery: '',
                      minPrice: 0,
                      maxPrice: 10000000,
                      location: '',
                    })}
                    variant="outline"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                {filteredCards.map((card) => (
                  <PropertyCard
                    key={card.id}
                    property={card}
                    onClick={handleCardClick}
                  />
                ))}
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Property Detail Overlay (authenticated) - hidden when investment flow is active */}
      {selectedProperty && !showInvestmentFlow && (
        <PropertyDetail
          property={selectedProperty}
          onClose={handleCloseDetail}
          onInvest={handleOpenInvestFlow}
        />
      )}

      {/* Property Detail Overlay (unauthenticated) - Invest button redirects to login */}
      {publicDetailProperty && (
        <PropertyDetail
          property={publicDetailProperty}
          onClose={handleClosePublicDetail}
          onInvest={handlePublicInvest}
        />
      )}

      {/* Investment Flow Overlay */}
      {showInvestmentFlow && investmentProperty && (
        <InvestmentFlow
          property={investmentProperty}
          onBack={handleCloseInvestFlow}
          onComplete={handlePropertyInvest}
        />
      )}

      {/* Featured Offer Detail Overlay */}
      {showFeaturedOfferDetail && selectedOfferingCard && (
        <OfferingDetail onClose={handleCloseFeaturedOffer} offeringId={selectedOfferingCard.id} />
      )}

      {/* Access Request Modal for Non-Accredited Investors */}
      {showAccessRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
              <ShieldOff className="w-6 h-6 text-amber-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Request Investment Access</h3>
                <p className="text-sm text-gray-600">
                  {(() => {
                    const prop = properties.find(p => p.id === accessRequestPropertyId);
                    return prop ? (prop.name || prop.title || prop.address) : 'this offering';
                  })()}
                </p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">
                As a non-accredited investor, you can request special access to invest in this offering.
                An administrator will review your request and, if approved, send you a private investment link via email.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message (optional)
                </label>
                <textarea
                  value={accessRequestMessage}
                  onChange={(e) => setAccessRequestMessage(e.target.value)}
                  placeholder="Share why you're interested in this offering..."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAccessRequestModal(false);
                  setAccessRequestMessage('');
                  setAccessRequestPropertyId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAccessRequest}
                disabled={accessRequestSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {accessRequestSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Centralized KYC gate modal — shared across all invest flows */}
      <KycGateModal />
    </div>
  );
}
